// Reading progress (top bar and rail percentage) and the outline that follows the reader. The main
// column (#learn-scroller) scrolls, not the window, so everything is measured against it. No React.

export function scroller(): HTMLElement {
  return document.getElementById('learn-scroller') || document.documentElement;
}

export function initReading(article: HTMLElement): void {
  const sc = scroller();
  const bar = document.querySelector<HTMLElement>('[data-learn-readbar] span');
  const pct = document.querySelector<HTMLElement>('[data-learn-percent]');
  const pctBar = document.querySelector<HTMLElement>('[data-learn-percent-bar]');

  const update = () => {
    const top = article.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop;
    const height = article.offsetHeight;
    const viewport = sc.clientHeight;
    const progress = height <= viewport ? 1 : Math.min(1, Math.max(0, (sc.scrollTop + viewport - top) / height));
    const p = Math.round(progress * 100);
    if (bar) bar.style.width = `${p}%`;
    if (pct) pct.textContent = `${p}%`;
    if (pctBar) pctBar.style.width = `${p}%`;
  };
  sc.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();

  // Outline: the heading nearest the top of the scroller is the active one.
  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('[data-learn-outline]'));
  if (links.length === 0 || !('IntersectionObserver' in window)) return;
  const byId = new Map(links.map((l) => [l.dataset.learnOutline!, l]));
  const headings = Array.from(byId.keys())
    .map((id) => document.getElementById(id))
    .filter((h): h is HTMLElement => !!h);
  const visible = new Set<string>();
  const setActive = (id: string) => {
    links.forEach((l) => l.classList.toggle('is-active', l.dataset.learnOutline === id));
  };
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) visible.add(e.target.id);
        else visible.delete(e.target.id);
      }
      const first = headings.find((h) => visible.has(h.id));
      if (first) {
        setActive(first.id);
        return;
      }
      const scTop = sc.getBoundingClientRect().top;
      let last: HTMLElement | null = null;
      for (const h of headings) {
        if (h.getBoundingClientRect().top - scTop < 140) last = h;
      }
      if (last) setActive(last.id);
    },
    { root: sc, rootMargin: '-70px 0px -60% 0px', threshold: [0, 1] }
  );
  headings.forEach((h) => io.observe(h));

  // Outline clicks scroll the scroller, not the window.
  links.forEach((l) =>
    l.addEventListener('click', (e) => {
      const target = document.getElementById(l.dataset.learnOutline!);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.replaceState(null, '', `#${l.dataset.learnOutline}`);
    })
  );
}

// The contents drawer on phones.
export function initNavDrawer(): void {
  const nav = document.getElementById('learn-nav');
  if (!nav) return;
  let backdrop: HTMLElement | null = null;
  const close = () => {
    nav.classList.remove('is-open');
    document.body.classList.remove('lb-nav-open');
    backdrop?.remove();
    backdrop = null;
  };
  const open = () => {
    nav.classList.add('is-open');
    document.body.classList.add('lb-nav-open');
    backdrop = document.createElement('div');
    backdrop.className = 'lb-backdrop';
    backdrop.addEventListener('click', close);
    document.body.appendChild(backdrop);
  };
  document.querySelectorAll('[data-learn-nav-open]').forEach((b) => b.addEventListener('click', open));
  document.querySelectorAll('[data-learn-nav-close]').forEach((b) => b.addEventListener('click', close));
  nav.querySelectorAll('a').forEach((a) => a.addEventListener('click', close));
}

// Light/dark toggle, written through window.SCPrefs (_Head.cshtml) so it applies site-wide.
export function initThemeToggle(): void {
  const w = window as unknown as { SCPrefs?: { setTheme: (t: string) => void } };
  document.querySelectorAll<HTMLButtonElement>('[data-learn-theme]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const current = document.documentElement.getAttribute('data-bs-theme');
      const next = current === 'dark' ? 'light' : 'dark';
      if (w.SCPrefs) w.SCPrefs.setTheme(next);
      else document.documentElement.setAttribute('data-bs-theme', next);
    })
  );
}

// Keep the left panel honest as the reader marks things.
export function setNavRead(slug: string, read: boolean): void {
  document.querySelectorAll<HTMLElement>(`[data-learn-nav-slug="${slug}"]`).forEach((el) => {
    el.classList.toggle('is-read', read);
  });
  const count = document.querySelector<HTMLElement>('[data-learn-read-count]');
  const items = document.querySelectorAll('[data-learn-nav-slug]');
  const readItems = document.querySelectorAll('[data-learn-nav-slug].is-read');
  if (count) count.textContent = String(readItems.length);
  const bar = document.querySelector<HTMLElement>('[data-learn-progress] .lb-nav-progress-bar span');
  if (bar && items.length) bar.style.width = `${Math.round((100 * readItems.length) / items.length)}%`;
  document.querySelectorAll<HTMLElement>('.lb-nav-section').forEach((sec) => {
    const total = sec.querySelectorAll('[data-learn-nav-slug]').length;
    const done = sec.querySelectorAll('[data-learn-nav-slug].is-read').length;
    const label = sec.querySelector('.lb-nav-section-count');
    if (label) label.textContent = `${done}/${total}`;
  });
}

export function setQuizPassed(slug: string): void {
  document.querySelectorAll<HTMLElement>(`[data-learn-quiz-badge="${slug}"]`).forEach((el) => el.classList.add('is-passed'));
}
